import { load } from "js-yaml";
import {
  Explanation,
  Game,
  GameWithTags,
  QuizSituation,
} from "../../../services/gameQuiz/gameQuiz.types";
import { GameTagDefinition } from "../../../services/gameTags/gameTags.types";
import {
  GAME_QUIZ_AUDIO_FOLDER,
  GAME_QUIZ_IMAGE_FOLDER,
  importAssetFromDriveUrl,
  isDriveUrl,
} from "../../../services/storage/storage.service";
import {
  generateGameId,
  generateOptionId,
  generateRowId,
  tokenizeWords,
} from "./gameTypes";

// Importador del YAML de quizzes (esquema nativo, ver Lineamientos sección 1).
// Parsea, valida y mapea a GameWithTags[] SIN tocar el backend: el resultado
// rellena el formulario y el usuario revisa antes de guardar.
//
// Archivos externos: los campos `image` y `audio` (opcionales e independientes
// en la pregunta, en cada opción/elemento y en la explicación) llevan una URL.
// Los dos pasan por el mismo camino: se extrae la URL ignorando la descripción
// que suele acompañarla, se asocia al elemento y, si es un link de Drive,
// `resolveDriveAssets` lo descarga y re-sube al bucket propio.

export interface YamlImportResult {
  title: string;
  // Optional header fields (SCRUM-1153): `tipo: normal` asks the form to also
  // create a "Quizzes Emmi" resource and append it to the course whose key is
  // `clave`. Empty string when absent.
  tipo: string;
  clave: string;
  situations?: QuizSituation[];
  games: GameWithTags[];
  questionClaves?: string[];
  errors: string[];
  warnings: string[];
}

const GAME_TYPES: Game["type"][] = [
  "multiple_choice",
  "true_false",
  "drag_and_drop",
  "drag_and_drop_horizontal",
  "fill_in_blanks",
  "categorization",
  "matching",
  "text_input",
  "text_input_ai",
  "image_labeling",
  "image_hotspot",
  "text_selection",
];

// Nombres del workflow viejo (en español) → tipo nativo, solo para dar un
// mensaje de error útil si llega un YAML con el esquema anterior.
const LEGACY_TYPE_HINTS: Record<string, Game["type"]> = {
  opcion_multiple: "multiple_choice",
  verdadero_falso: "true_false",
  secuenciar_vertical: "drag_and_drop",
  secuenciar_horizontal: "drag_and_drop_horizontal",
  completar_espacios: "fill_in_blanks",
  categorizacion: "categorization",
  emparejar: "matching",
  texto_corto: "text_input",
  texto_corto_ia: "text_input_ai",
  etiquetar_imagen: "image_labeling",
  seleccionar_area: "image_hotspot",
  seleccionar_texto: "text_selection",
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

// YAML parsea "6" como number y "sí"/"no" como boolean: los campos de texto
// aceptan cualquier escalar y lo convierten.
const toText = (v: unknown): string => {
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
};

const toTextList = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(toText).filter((s) => s.length > 0);
  const single = toText(v);
  return single ? [single] : [];
};

// keyIdeas/keywords son texto plano en la plataforma; si el YAML trae lista,
// se une para no perder contenido.
const toJoinedText = (v: unknown, separator: string): string =>
  Array.isArray(v) ? v.map(toText).filter(Boolean).join(separator) : toText(v);

const norm = (s: string): string => s.trim().toLowerCase();

// Campos del YAML que apuntan a un archivo externo. `image` y `audio` pasan
// por exactamente la misma lógica; el tipo solo decide cómo se lo nombra en
// los avisos y a qué carpeta del bucket va al resolver links de Drive.
type AssetKind = "image" | "audio";

const ASSET_NAMES: Record<AssetKind, string> = {
  image: "imagen",
  audio: "audio",
};

// Placeholders que a veces se escriben a mano en el YAML en vez de dejar el
// campo `image`/`audio` vacío u omitido (p.ej. al redactar el contenido antes
// de tener el link de Drive). Se tratan como "sin archivo" y se avisa.
const ASSET_PLACEHOLDERS = new Set([
  "sin imagen",
  "sin audio",
  "s/n",
  "s/i",
  "n/a",
  "na",
  "-",
  "ninguna",
  "ninguno",
]);

const URL_REGEX = /https?:\/\/[^\s"'<>]+/i;

const shorten = (s: string): string =>
  s.length > 60 ? `${s.slice(0, 57)}...` : s;

// Devuelve la URL del archivo, o "" si el elemento no trae ese archivo. El
// valor puede venir con texto además del link (la descripción de la imagen
// que se redacta a continuación): se ignora y queda solo la URL.
const toAssetUrl = (
  v: unknown,
  kind: AssetKind,
  label: string,
  warnings: string[],
): string => {
  const text = toText(v);
  if (!text) return "";
  if (ASSET_PLACEHOLDERS.has(norm(text))) {
    warnings.push(
      `${label}: el campo \`${kind}\` tenía el placeholder "${text}"; se importó sin ${ASSET_NAMES[kind]}.`,
    );
    return "";
  }
  const match = text.match(URL_REGEX);
  if (!match) {
    warnings.push(
      `${label}: el campo \`${kind}\` no contiene una URL ("${shorten(text)}"); se importó sin ${ASSET_NAMES[kind]}.`,
    );
    return "";
  }
  // Puntuación pegada al final del link cuando la descripción sigue de corrido.
  return match[0].replace(/[.,;]+$/, "");
};

// `image` y `audio` opcionales de cualquier elemento (pregunta, opción, item,
// lado de un par, explicación). Solo agrega las claves que traen archivo.
const toAssets = (
  o: Record<string, unknown>,
  label: string,
  warnings: string[],
): { image?: string; audio?: string } => {
  const image = toAssetUrl(o.image, "image", label, warnings);
  const audio = toAssetUrl(o.audio, "audio", label, warnings);
  return { ...(image ? { image } : {}), ...(audio ? { audio } : {}) };
};

// Línea `image:`/`audio:` (suelta o abriendo un item de lista): indentación,
// guion opcional, clave y todo lo que sigue a los dos puntos.
const ASSET_LINE_REGEX = /^(\s*)(-\s+)?(image|audio|imagen)\s*:(.*)$/;
// Valor entre comillas seguido de texto que no es comentario.
const QUOTED_VALUE_WITH_TRAILING_REGEX =
  /^(\s*)("[^"]*"|'[^']*')\s*([^#\s].*)$/;
const BLOCK_SCALAR_REGEX = /^[|>]/;
const MAPPING_KEY_REGEX = /^[^\s#-][^:]*:(\s|$)/;
const SEQUENCE_ITEM_REGEX = /^-(\s|$)/;

// Los YAML de contenido traen la descripción de la imagen pegada al link,
// fuera de las comillas o en la línea siguiente. Ninguna de las dos formas es
// YAML válido (js-yaml corta con "bad indentation" / "multiline key"), así
// que antes de parsear se saca ese texto de las líneas `image:`/`audio:`:
//
//   image: "https://.../foto.png" un niño observando una planta
//   image: "https://.../foto.png"
//   Un niño observando una planta.
//
// Solo se tocan esas dos claves y las líneas sueltas que las siguen (mismo
// nivel o más adentro, sin forma de clave ni de item); el resto del archivo
// llega intacto al parser. Las líneas descartadas se reemplazan por
// comentarios para que los números de línea de los errores no se muevan.
export function sanitizeAssetLines(source: string): string {
  // Columna de la clave `image`/`audio` anterior mientras las líneas que
  // siguen puedan ser su descripción; null cuando no aplica.
  let assetColumn: number | null = null;

  return source
    .split(/\r?\n/)
    .map((line) => {
      const assetMatch = line.match(ASSET_LINE_REGEX);
      if (assetMatch) {
        const [, indent, dash = "", key, rest] = assetMatch;
        const value = rest.trim();
        assetColumn =
          value && !BLOCK_SCALAR_REGEX.test(value)
            ? indent.length + dash.length
            : null;
        const trailing = rest.match(QUOTED_VALUE_WITH_TRAILING_REGEX);
        if (!trailing) return line;
        const [, space, quoted] = trailing;
        return `${indent}${dash}${key}:${space}${quoted}`;
      }

      if (assetColumn === null) return line;
      const trimmed = line.trim();
      if (!trimmed) return line;
      const column = line.length - line.trimStart().length;
      if (
        column < assetColumn ||
        trimmed.startsWith("#") ||
        SEQUENCE_ITEM_REGEX.test(trimmed) ||
        MAPPING_KEY_REGEX.test(trimmed)
      ) {
        assetColumn = null;
        return line;
      }
      return `${" ".repeat(column)}# ${trimmed}`;
    })
    .join("\n");
}

// Only `tipo: normal` triggers the resource + course assignment on save; any
// other value keeps the plain import.
export const isNormalQuizType = (tipo: string): boolean =>
  tipo.trim().toLowerCase() === "normal";

export const isExamQuizType = (tipo: string): boolean =>
  tipo.trim().toLowerCase() === "examen";

const toPositiveNumber = (value: unknown): number | null => {
  const number =
    typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) && number > 0
    ? number
    : null;
};

const toCourseId = (value: unknown): number | null => {
  const number = toPositiveNumber(value);
  return number !== null && Number.isInteger(number) ? number : null;
};

const EXAM_FIELD_KEYS = ["puntos", "puntaje", "cursoID", "curso"];
const EXAM_SIGNAL_KEYS = [...EXAM_FIELD_KEYS, "situacion", "situacionImagen"];

const carriesExamSignals = (rawGame: unknown): boolean =>
  isRecord(rawGame) &&
  EXAM_SIGNAL_KEYS.some((key) => rawGame[key] !== undefined);

const carriesExamFields = (rawGame: unknown): boolean =>
  isRecord(rawGame) &&
  EXAM_FIELD_KEYS.some((key) => rawGame[key] !== undefined);

const firstDefined = (...values: unknown[]): unknown =>
  values.find((value) => value !== undefined);

function examFieldsOf(
  rawGame: Record<string, unknown>,
  label: string,
  errors: string[],
): { points: number; course_id: number } | null {
  const points = toPositiveNumber(
    firstDefined(rawGame.puntos, rawGame.puntaje),
  );
  const courseId = toCourseId(firstDefined(rawGame.cursoID, rawGame.curso));
  if (points === null) {
    errors.push(
      `${label}: falta \`puntos\` (o \`puntaje\`), un número mayor que 0.`,
    );
  }
  if (courseId === null) {
    errors.push(
      `${label}: falta \`cursoID\` (o \`curso\`), el ID del curso que evalúa.`,
    );
  }
  return points === null || courseId === null
    ? null
    : { points, course_id: courseId };
}

function parseSituations(
  value: unknown,
  errors: string[],
  warnings: string[],
): { situations: QuizSituation[]; ids: Set<string> } {
  const situations: QuizSituation[] = [];
  const ids = new Set<string>();
  if (value === undefined || value === null) return { situations, ids };
  if (!Array.isArray(value)) {
    errors.push(
      "`situaciones` debe ser una lista de situaciones (`id`, `texto` e `imagen` opcional).",
    );
    return { situations, ids };
  }
  value.forEach((item, index) => {
    const label = `Situación ${index + 1}`;
    if (!isRecord(item)) {
      errors.push(`${label}: no es un objeto válido.`);
      return;
    }
    const id = toText(item.id);
    if (!id) {
      errors.push(`${label}: falta el \`id\`.`);
      return;
    }
    if (ids.has(id)) {
      errors.push(`${label}: el \`id\` «${id}» ya lo usa otra situación.`);
      return;
    }
    ids.add(id);
    const text = toText(item.texto);
    if (!text) {
      errors.push(`${label}: falta el \`texto\`.`);
      return;
    }
    const image = toAssetUrl(item.imagen, "image", label, warnings);
    situations.push({ id, text, image_url: image || null });
  });
  return { situations, ids };
}

// Exported so the Hub de subida, which assigns every quiz to the course of its
// tracker row, can tell this form-only rule apart from real YAML errors.
export const TIPO_NORMAL_NEEDS_CLAVE =
  "`tipo: normal` necesita el campo `clave` (la clave del curso, ej. P5_9_GRAM) para asignar el recurso.";

export const isCourseClaveRule = (error: string): boolean =>
  error === TIPO_NORMAL_NEEDS_CLAVE;

interface InlineSituation {
  text: string;
  image: string;
}

function inlineSituationOf(
  rawGame: Record<string, unknown>,
  label: string,
  errors: string[],
  warnings: string[],
): InlineSituation | null {
  const text = toText(rawGame.situacion);
  const image = toAssetUrl(rawGame.situacionImagen, "image", label, warnings);
  if (!text && image) {
    errors.push(
      `${label}: \`situacionImagen\` necesita el texto de la \`situacion\`.`,
    );
    return null;
  }
  return text ? { text, image } : null;
}

const situationKey = ({ text, image }: InlineSituation): string =>
  `${text}\n${image}`;

function checkQuestionClave(
  clave: string,
  courseId: number | undefined,
  label: string,
  clavesByCourse: Map<number, string>,
  errors: string[],
): void {
  if (!clave || courseId === undefined) return;
  const known = clavesByCourse.get(courseId);
  if (known === undefined) {
    clavesByCourse.set(courseId, clave);
  } else if (known.toUpperCase() !== clave.toUpperCase()) {
    errors.push(
      `${label}: la \`clave\` «${clave}» no coincide con la «${known}» que otra pregunta le da al curso ${courseId}.`,
    );
  }
}

export interface ParseQuizYamlOptions {
  exam?: boolean;
}

export function parseQuizYaml(
  source: string,
  tagDefs: GameTagDefinition[],
  options: ParseQuizYamlOptions = {},
): YamlImportResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const result: YamlImportResult = {
    title: "",
    tipo: "",
    clave: "",
    situations: [],
    games: [],
    errors,
    warnings,
  };

  let raw: unknown;
  try {
    raw = load(sanitizeAssetLines(source));
  } catch (e) {
    errors.push(
      `YAML inválido: ${e instanceof Error ? e.message : "error de sintaxis"}`,
    );
    return result;
  }
  if (!isRecord(raw)) {
    errors.push("El archivo no tiene la estructura esperada (objeto YAML).");
    return result;
  }

  result.title = toText(raw.title);
  if (!result.title) errors.push("Falta el campo `title` (título del quiz).");

  result.tipo = toText(raw.tipo);
  result.clave = toText(raw.clave);
  if (isNormalQuizType(result.tipo) && !result.clave) {
    errors.push(TIPO_NORMAL_NEEDS_CLAVE);
  }
  if (
    !result.tipo &&
    (options.exam ||
      (Array.isArray(raw.games) && raw.games.some(carriesExamSignals)))
  ) {
    result.tipo = "examen";
  }
  const isExam = isExamQuizType(result.tipo);
  const inlineSituations = isExam && raw.situaciones === undefined;
  const questionClaves: string[] = [];
  const clavesByCourse = new Map<number, string>();
  let previousInline: { key: string; id: string } | null = null;
  const situationIds = new Set<string>();
  if (isExam && !inlineSituations) {
    const parsed = parseSituations(raw.situaciones, errors, warnings);
    result.situations = parsed.situations;
    parsed.ids.forEach((id) => situationIds.add(id));
  } else if (raw.situaciones !== undefined) {
    warnings.push("`situaciones` solo se usa con `tipo: examen`; se ignora.");
  }
  const usedSituations = new Set<string>();
  let previousSituation: string | null = null;

  const resolveTag = (
    name: string,
    category: GameTagDefinition["category"],
    context: string,
  ): number | null => {
    const def = tagDefs.find(
      (t) => t.category === category && norm(t.name) === norm(name),
    );
    if (!def) {
      warnings.push(
        `${context}: tag ${category} "${name}" no existe en la plataforma; se omite.`,
      );
      return null;
    }
    return def.id;
  };

  const resolveTagList = (
    v: unknown,
    category: GameTagDefinition["category"],
    context: string,
  ): number[] =>
    toTextList(v)
      .map((name) => resolveTag(name, category, context))
      .filter((id): id is number => id !== null);

  // Los tags a nivel de quiz se retiraron: solo existen por pregunta.
  if (raw.tags_blooms !== undefined || raw.tags_pisa !== undefined) {
    warnings.push(
      "Los tags a nivel de quiz (`tags_blooms`/`tags_pisa` en la cabecera) ya no se usan; se ignoran. Etiquetar cada pregunta con `bloom`/`pisa`.",
    );
  }

  if (!Array.isArray(raw.games) || raw.games.length === 0) {
    errors.push("Falta la lista `games` con al menos una pregunta.");
    return result;
  }

  raw.games.forEach((rawGame, index) => {
    const label = `Pregunta ${index + 1}`;
    if (!isRecord(rawGame)) {
      previousSituation = null;
      errors.push(`${label}: no es un objeto válido.`);
      return;
    }

    const inline = inlineSituations
      ? inlineSituationOf(rawGame, label, errors, warnings)
      : null;
    const situationId =
      isExam && !inlineSituations ? toText(rawGame.situacion) : "";
    const followsSituation =
      situationId !== "" && situationId === previousSituation;
    previousSituation = situationId || null;
    let linkedSituation: string | null = null;
    if (
      !isExam &&
      (rawGame.situacion !== undefined || rawGame.situacionImagen !== undefined)
    ) {
      warnings.push(
        `${label}: \`situacion\` solo se usa en un examen; se ignora.`,
      );
    } else if (
      isExam &&
      !inlineSituations &&
      rawGame.situacionImagen !== undefined
    ) {
      warnings.push(
        `${label}: \`situacionImagen\` no se usa con \`situaciones\` (la imagen va en la \`imagen\` de la situación); se ignora.`,
      );
    } else if (situationId && !situationIds.has(situationId)) {
      errors.push(
        `${label}: la \`situacion\` «${situationId}» no está en \`situaciones\`.`,
      );
    } else if (
      situationId &&
      usedSituations.has(situationId) &&
      !followsSituation
    ) {
      errors.push(
        `${label}: las preguntas de la situación «${situationId}» deben ir seguidas.`,
      );
    } else if (situationId) {
      usedSituations.add(situationId);
      linkedSituation = situationId;
    }

    const type = toText(rawGame.type) as Game["type"];
    if (!GAME_TYPES.includes(type)) {
      const hint = LEGACY_TYPE_HINTS[norm(toText(rawGame.type))];
      errors.push(
        `${label}: tipo "${toText(rawGame.type)}" desconocido.` +
          (hint ? ` ¿Quisiste decir "${hint}"? (usar el esquema nativo)` : ""),
      );
      return;
    }

    const question = toText(rawGame.question);
    if (!question) {
      errors.push(`${label}: falta el enunciado (\`question\`).`);
      return;
    }

    const game = buildGame(type, question, rawGame, label, errors, warnings);
    if (!game) return;

    const { image, audio } = toAssets(rawGame, label, warnings);
    if (image) game.image = image;
    // A nivel pregunta el audio vive en `questionAudio`, el mismo campo que
    // llena el botón de TTS / subida manual del editor.
    if (audio) game.questionAudio = audio;

    const explanation = buildExplanation(rawGame, label, errors, warnings);
    if (explanation) game.explanation = explanation;

    if (isExam) {
      const examFields = examFieldsOf(rawGame, label, errors);
      if (examFields) Object.assign(game, examFields);
      if (linkedSituation) game.situation_id = linkedSituation;
      if (inline) {
        const key = situationKey(inline);
        if (previousInline?.key !== key) {
          const id = `situacion-${(result.situations ?? []).length + 1}`;
          result.situations?.push({
            id,
            text: inline.text,
            image_url: inline.image || null,
          });
          previousInline = { key, id };
        }
        game.situation_id = previousInline.id;
      } else {
        previousInline = null;
      }
      const clave = toText(rawGame.clave);
      checkQuestionClave(
        clave,
        examFields?.course_id,
        label,
        clavesByCourse,
        errors,
      );
      questionClaves.push(clave);
    } else if (carriesExamFields(rawGame)) {
      warnings.push(
        `${label}: \`puntos\` y \`cursoID\` (o \`puntaje\` y \`curso\`) solo se usan en un examen; se ignoran.`,
      );
    }

    const tagIds = [
      ...resolveTagList(rawGame.bloom, "BLOOMS", label),
      ...resolveTagList(rawGame.pisa, "PISA", label),
    ];

    result.games.push(
      tagIds.length > 0 ? { ...game, tagIds } : (game as GameWithTags),
    );
  });

  if (isExam) result.questionClaves = questionClaves;
  (inlineSituations ? [] : (result.situations ?? []))
    .filter((situation) => !usedSituations.has(situation.id))
    .forEach((situation) =>
      errors.push(`Situación «${situation.id}»: ninguna pregunta la usa.`),
    );

  return result;
}

function buildExplanation(
  rawGame: Record<string, unknown>,
  label: string,
  errors: string[],
  warnings: string[],
): Explanation | null {
  const raw = rawGame.explanation;
  if (raw === undefined || raw === null) return null;
  if (!isRecord(raw)) {
    errors.push(`${label}: \`explanation\` debe ser un objeto con \`answer\`.`);
    return null;
  }
  // `text` se acepta como alias de `answer` (es como figura en los
  // lineamientos de contenido).
  const answer = toText(raw.answer) || toText(raw.text);
  if (!answer) {
    errors.push(
      `${label}: \`explanation.answer\` (o \`explanation.text\`) es obligatoria.`,
    );
    return null;
  }
  if (answer.length > 512) {
    warnings.push(
      `${label}: la explicación supera 512 caracteres (${answer.length}); acortarla en el editor.`,
    );
  }
  const rows = (Array.isArray(raw.rows) ? raw.rows : [])
    .map((r) => ({
      text: toText(isRecord(r) ? r.text : r),
      image: toAssetUrl(isRecord(r) ? r.image : "", "image", label, warnings),
    }))
    .filter((r) => r.text || r.image)
    .map((r) => ({ id: generateRowId(), text: r.text, image: r.image }));
  // La explicación no tiene imagen propia: una `image` a su nivel se vuelve
  // una fila más, que es donde el editor y el juego muestran imágenes.
  const { image, audio } = toAssets(raw, label, warnings);
  if (image) rows.push({ id: generateRowId(), text: "", image });
  return { answer, audio: audio ?? "", rows };
}

// eslint-disable-next-line complexity
function buildGame(
  type: Game["type"],
  question: string,
  rawGame: Record<string, unknown>,
  label: string,
  errors: string[],
  warnings: string[],
): Game | null {
  const id = generateGameId();

  switch (type) {
    case "multiple_choice": {
      const rawOptions = Array.isArray(rawGame.options) ? rawGame.options : [];
      const options = rawOptions
        .map((o) => {
          if (isRecord(o)) {
            return {
              id: generateOptionId(),
              text: toText(o.text),
              correct: o.correct === true,
              ...toAssets(o, label, warnings),
            };
          }
          return { id: generateOptionId(), text: toText(o), correct: false };
        })
        .filter((o) => o.text || "image" in o);
      if (options.length < 2) {
        errors.push(`${label}: \`options\` necesita al menos 2 opciones.`);
        return null;
      }
      if (!options.some((o) => o.correct)) {
        errors.push(
          `${label}: ninguna opción tiene \`correct: true\` (se necesita al menos una).`,
        );
        return null;
      }
      return { id, type, question, options };
    }

    case "true_false": {
      const categoryNames = toTextList(rawGame.categories);
      if (categoryNames.length !== 2) {
        errors.push(
          `${label}: \`categories\` debe tener exactamente 2 nombres.`,
        );
        return null;
      }
      const categories: [
        { id: string; label: string },
        { id: string; label: string },
      ] = [
        { id: generateOptionId(), label: categoryNames[0] },
        { id: generateOptionId(), label: categoryNames[1] },
      ];
      const rawStatements = Array.isArray(rawGame.statements)
        ? rawGame.statements
        : [];
      const statements: {
        id: string;
        text: string;
        correctCategoryId: string;
        image?: string;
        audio?: string;
      }[] = [];
      for (const s of rawStatements) {
        if (!isRecord(s)) continue;
        const text = toText(s.text);
        if (!text) continue;
        const categoryId = resolveCategoryRef(s.category, categoryNames, [
          categories[0].id,
          categories[1].id,
        ]);
        if (!categoryId) {
          errors.push(
            `${label}: la afirmación "${text}" tiene una \`category\` inválida (usar nombre o número 1/2).`,
          );
          return null;
        }
        statements.push({
          id: generateOptionId(),
          text,
          correctCategoryId: categoryId,
          ...toAssets(s, label, warnings),
        });
      }
      if (statements.length === 0) {
        errors.push(`${label}: \`statements\` necesita al menos 1 afirmación.`);
        return null;
      }
      return { id, type, question, categories, statements };
    }

    case "categorization": {
      const categoryNames = toTextList(rawGame.categories);
      if (categoryNames.length === 0) {
        errors.push(`${label}: \`categories\` necesita al menos 1 categoría.`);
        return null;
      }
      const categories = categoryNames.map((name) => ({
        id: generateOptionId(),
        name,
      }));
      const categoryIds = categories.map((c) => c.id);
      const rawItems = Array.isArray(rawGame.items) ? rawGame.items : [];
      const items: {
        id: string;
        text: string;
        categoryId: string;
        image?: string;
        audio?: string;
      }[] = [];
      for (const it of rawItems) {
        if (!isRecord(it)) continue;
        const text = toText(it.text);
        const assets = toAssets(it, label, warnings);
        if (!text && !assets.image) continue;
        const categoryId = resolveCategoryRef(
          it.category,
          categoryNames,
          categoryIds,
        );
        if (!categoryId) {
          errors.push(
            `${label}: el item "${text || assets.image}" tiene una \`category\` inválida (usar nombre o número 1-${categoryNames.length}).`,
          );
          return null;
        }
        items.push({
          id: generateOptionId(),
          text,
          categoryId,
          ...assets,
        });
      }
      if (items.length === 0) {
        errors.push(`${label}: \`items\` necesita al menos 1 elemento.`);
        return null;
      }
      const distractors = buildTextImageList(
        rawGame.distractors,
        label,
        warnings,
      );
      return { id, type, question, categories, items, distractors };
    }

    case "matching": {
      const rawPairs = Array.isArray(rawGame.pairs) ? rawGame.pairs : [];
      const pairs = rawPairs
        .filter(isRecord)
        .map((p) => ({
          id: generateOptionId(),
          left: buildTextImageSide(p.left, label, warnings),
          right: buildTextImageSide(p.right, label, warnings),
        }))
        .filter(
          (p) =>
            (p.left.text || p.left.image) && (p.right.text || p.right.image),
        );
      if (pairs.length === 0) {
        errors.push(
          `${label}: \`pairs\` necesita al menos 1 par con \`left\` y \`right\`.`,
        );
        return null;
      }
      if (rawGame.distractors !== undefined) {
        warnings.push(
          `${label}: matching ya no admite \`distractors\`; se ignoran.`,
        );
      }
      return { id, type, question, pairs };
    }

    case "fill_in_blanks": {
      const text = toText(rawGame.text);
      const blankCount = (text.match(/\{\}/g) ?? []).length;
      if (blankCount === 0) {
        errors.push(
          `${label}: \`text\` debe incluir al menos un hueco marcado con {}.`,
        );
        return null;
      }
      const rawOptions = Array.isArray(rawGame.options) ? rawGame.options : [];
      const options = buildTextImageList(rawOptions, label, warnings);
      if (options.length === 0) {
        errors.push(`${label}: falta \`options\` (el banco de fichas).`);
        return null;
      }
      const answers = toTextList(rawGame.answers);
      if (answers.length !== blankCount) {
        errors.push(
          `${label}: \`answers\` tiene ${answers.length} respuestas pero el texto tiene ${blankCount} huecos {}.`,
        );
        return null;
      }
      const blanks: {
        id: string;
        position: number;
        correctOptionId: string;
      }[] = [];
      for (let i = 0; i < answers.length; i++) {
        const option = options.find((o) => norm(o.text) === norm(answers[i]));
        if (!option) {
          errors.push(
            `${label}: la respuesta "${answers[i]}" no existe en \`options\`.`,
          );
          return null;
        }
        blanks.push({
          id: generateOptionId(),
          position: i,
          correctOptionId: option.id,
        });
      }
      return { id, type, question, text, blanks, options };
    }

    case "drag_and_drop": {
      const items = toItemList(rawGame.items, label, warnings);
      if (items.length < 2) {
        errors.push(
          `${label}: \`items\` necesita al menos 2 elementos (en el orden correcto).`,
        );
        return null;
      }
      return { id, type, question, items };
    }

    case "drag_and_drop_horizontal": {
      const rawItems = Array.isArray(rawGame.items) ? rawGame.items : [];
      const items = rawItems
        .map((it) => {
          if (isRecord(it)) {
            const slotLabel = toText(it.slotLabel);
            return {
              id: generateOptionId(),
              text: toText(it.text),
              ...(slotLabel ? { slotLabel } : {}),
              ...toAssets(it, label, warnings),
            };
          }
          return { id: generateOptionId(), text: toText(it) };
        })
        .filter((it) => it.text || "image" in it);
      if (items.length < 2) {
        errors.push(
          `${label}: \`items\` necesita al menos 2 elementos (en el orden correcto).`,
        );
        return null;
      }
      return { id, type, question, items };
    }

    case "text_input": {
      const answers = toTextList(rawGame.answers);
      if (answers.length === 0) {
        errors.push(`${label}: \`answers\` necesita al menos 1 respuesta.`);
        return null;
      }
      const correctAnswers = answers.map((t) => ({
        id: generateOptionId(),
        text: t,
      }));
      const game: Extract<Game, { type: "text_input" }> = {
        id,
        type,
        question,
        correctAnswers,
      };
      if (rawGame.caseSensitive === true) game.caseSensitive = true;
      if (rawGame.accentSensitive === true) game.accentSensitive = true;
      return game;
    }

    case "text_input_ai": {
      const modelAnswer = toText(rawGame.modelAnswer);
      const keyIdeas = toJoinedText(rawGame.keyIdeas, "\n");
      if (!modelAnswer || !keyIdeas) {
        errors.push(
          `${label}: \`modelAnswer\` y \`keyIdeas\` son obligatorios en text_input_ai.`,
        );
        return null;
      }
      return {
        id,
        type,
        question,
        modelAnswer,
        keyIdeas,
        keywords: toJoinedText(rawGame.keywords, ", "),
        subject: toText(rawGame.subject),
        grade: toText(rawGame.grade),
      };
    }

    case "image_labeling": {
      const labelTexts = toTextList(rawGame.labels);
      if (labelTexts.length === 0) {
        errors.push(`${label}: \`labels\` necesita al menos 1 etiqueta.`);
        return null;
      }
      // Las zonas se dibujan a mano: todas las etiquetas nacen sin asignar y
      // las que queden sin zona funcionan como distractores.
      const labels = [...labelTexts, ...toTextList(rawGame.distractors)].map(
        (text) => ({
          id: generateOptionId(),
          text,
          correctHotspotId: "",
        }),
      );
      warnings.push(
        `${label} (image_labeling): subir la imagen, dibujar las zonas y asignar cada etiqueta en el editor.`,
      );
      return { id, type, question, image: "", hotspots: [], labels };
    }

    case "image_hotspot": {
      warnings.push(
        `${label} (image_hotspot): subir la imagen y marcar las zonas correctas e incorrectas en el editor.`,
      );
      return { id, type, question, image: "", hotspots: [] };
    }

    case "text_selection": {
      const text = toText(rawGame.text);
      if (!text) {
        errors.push(`${label}: falta \`text\` (la frase a seleccionar).`);
        return null;
      }
      const mode = toText(rawGame.mode) || "words";
      if (mode !== "words" && mode !== "fragments") {
        errors.push(
          `${label}: \`mode\` debe ser "words" o "fragments" (llegó "${mode}").`,
        );
        return null;
      }
      if (mode === "words") {
        const parts = tokenizeWords(text);
        const correctWords = toTextList(rawGame.correct).map(norm);
        let matched = 0;
        parts.forEach((p) => {
          if (p.selectable && correctWords.includes(norm(p.text))) {
            p.correct = true;
            matched++;
          }
        });
        for (const w of correctWords) {
          if (!parts.some((p) => p.selectable && norm(p.text) === w)) {
            warnings.push(
              `${label}: la palabra correcta "${w}" no aparece en el texto; revisar en el editor.`,
            );
          }
        }
        if (matched === 0) {
          warnings.push(
            `${label} (text_selection): marcar las palabras correctas en el editor.`,
          );
        }
        return { id, type, question, text, authoringMode: "words", parts };
      }
      const rawFragments = Array.isArray(rawGame.fragments)
        ? rawGame.fragments
        : [];
      const fragments = rawFragments
        .map((f) => {
          if (isRecord(f)) {
            return { text: toText(f.text), correct: f.correct === true };
          }
          return { text: toText(f), correct: false };
        })
        .filter((f) => f.text);
      if (fragments.length === 0) {
        errors.push(
          `${label}: en modo fragments falta \`fragments\` con al menos 1 fragmento.`,
        );
        return null;
      }
      if (!fragments.some((f) => f.correct)) {
        warnings.push(
          `${label} (text_selection): ningún fragmento tiene \`correct: true\`; marcarlos en el editor.`,
        );
      }
      // Mismo criterio que el editor: fragmentos unidos con un espacio, sin
      // espacio final.
      const parts = fragments.map((f, i) => ({
        id: generateOptionId(),
        text: f.text,
        selectable: true,
        correct: f.correct,
        trailing: i < fragments.length - 1 ? " " : "",
      }));
      return { id, type, question, text, authoringMode: "fragments", parts };
    }
  }
}

// `category` puede venir como nombre ("Mamíferos") o número 1-based (1, 2...).
function resolveCategoryRef(
  ref: unknown,
  names: string[],
  ids: string[],
): string | null {
  if (typeof ref === "number") {
    return Number.isInteger(ref) && ref >= 1 && ref <= ids.length
      ? ids[ref - 1]
      : null;
  }
  const name = toText(ref);
  if (!name) return null;
  const index = names.findIndex((n) => norm(n) === norm(name));
  return index >= 0 ? ids[index] : null;
}

// left/right de un pair (matching) siempre son objeto:
// `{ text, image?, audio? }`.
function buildTextImageSide(
  v: unknown,
  label: string,
  warnings: string[],
): { text: string; image?: string; audio?: string } {
  if (!isRecord(v)) return { text: "" };
  return { text: toText(v.text), ...toAssets(v, label, warnings) };
}

// distractors (categorization) y options (fill_in_blanks): lista de string
// simple u objeto `{ text, image?, audio? }`.
function buildTextImageList(
  v: unknown,
  label: string,
  warnings: string[],
): { id: string; text: string; image?: string; audio?: string }[] {
  const list = Array.isArray(v) ? v : [];
  return list
    .map((d) => {
      if (isRecord(d)) {
        return {
          id: generateOptionId(),
          text: toText(d.text),
          ...toAssets(d, label, warnings),
        };
      }
      return { id: generateOptionId(), text: toText(d) };
    })
    .filter((d) => d.text);
}

function toItemList(
  v: unknown,
  label: string,
  warnings: string[],
): { id: string; text: string; image?: string; audio?: string }[] {
  const list = Array.isArray(v) ? v : [];
  return list
    .map((it) => {
      if (isRecord(it)) {
        return {
          id: generateOptionId(),
          text: toText(it.text),
          ...toAssets(it, label, warnings),
        };
      }
      return { id: generateOptionId(), text: toText(it) };
    })
    .filter((it) => it.text || "image" in it);
}

// Claves del juego ya mapeado que apuntan a un archivo, con la carpeta del
// bucket que le corresponde a cada una. `questionAudio` es donde termina el
// `audio` a nivel pregunta.
const ASSET_KEY_FOLDERS = new Map<string, string>([
  ["image", GAME_QUIZ_IMAGE_FOLDER],
  ["audio", GAME_QUIZ_AUDIO_FOLDER],
  ["questionAudio", GAME_QUIZ_AUDIO_FOLDER],
  ["image_url", GAME_QUIZ_IMAGE_FOLDER],
]);

// Recorre genéricamente cualquier objeto/array del juego buscando propiedades
// de archivo (`image`, `audio`, `questionAudio`) con un link de Drive, y
// colecciona su ubicación para resolverlas todas en paralelo. Genérico (no un
// switch por tipo) para no desincronizarse si se agregan campos nuevos a
// algún tipo de pregunta.
function collectDriveAssetPaths(
  node: unknown,
  onFound: (
    currentUrl: string,
    folder: string,
    setResolved: (url: string) => void,
  ) => void,
): void {
  if (Array.isArray(node)) {
    node.forEach((item) => collectDriveAssetPaths(item, onFound));
    return;
  }
  if (!isRecord(node)) return;

  for (const key of Object.keys(node)) {
    const value = node[key];
    const folder = ASSET_KEY_FOLDERS.get(key);
    if (folder && typeof value === "string" && isDriveUrl(value)) {
      onFound(value, folder, (url) => {
        node[key] = url;
      });
      continue;
    }
    collectDriveAssetPaths(value, onFound);
  }
}

export interface DriveResolutionProgress {
  total: number;
  done: number;
}

// Reemplaza, in-place, cada campo de archivo (`image`, `audio`,
// `questionAudio`) que sea un link de Google Drive por la URL del bucket
// propio (descarga vía la cuenta de servicio + re-sube a la carpeta de
// imágenes o de audios según el campo). El mismo link repetido en varios
// elementos se descarga una sola vez. Devuelve cuántos archivos se
// resolvieron y cuáles fallaron (el campo original se deja intacto en los que
// fallan, para poder reintentarlos a mano).
export async function resolveDriveAssets(
  content: unknown,
  onProgress?: (progress: DriveResolutionProgress) => void,
): Promise<{ resolved: number; failed: string[] }> {
  const pending = new Map<
    string,
    { url: string; folder: string; apply: ((url: string) => void)[] }
  >();
  collectDriveAssetPaths(content, (url, folder, setResolved) => {
    const key = `${folder}\n${url}`;
    const entry = pending.get(key);
    if (entry) {
      entry.apply.push(setResolved);
    } else {
      pending.set(key, { url, folder, apply: [setResolved] });
    }
  });

  const entries = Array.from(pending.values());
  if (entries.length === 0) return { resolved: 0, failed: [] };

  let done = 0;
  const failed: string[] = [];

  await Promise.all(
    entries.map(async ({ url, folder, apply }) => {
      const resolvedUrl = await importAssetFromDriveUrl(url, folder);
      if (resolvedUrl) {
        apply.forEach((set) => set(resolvedUrl));
      } else {
        failed.push(url);
      }
      done += 1;
      onProgress?.({ total: entries.length, done });
    }),
  );

  return { resolved: entries.length - failed.length, failed };
}
